import NewOrganizations from "@/Components/NewOrganizations";
import NewBlogs from "@/Components/NewBlogs";
import AuthenticatedLayout from "@/Layouts/AuthenticatedLayout";
import { Head, Link } from "@inertiajs/react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState, useRef, useEffect } from "react";
import ApiService from "@/Services/ApiService";
import BlogCard from "@/Components/BlogCard";
import CarouselModal from "@/Components/Modals/CarouselModal";
import SharePostModal from "@/Components/Modals/SharePostModal";
import EditPostModal from "@/Components/EditPostModal";
import PostSkelotonCard from "@/Components/Skeletons/PostSkeletonCard";
import { usePage } from "@inertiajs/react";
import BlogPreviewCarousel from "@/Components/Carousels/BlogPreviewCarousel";
import { AutoSizer } from "react-virtualized";
import UserOne from "../../images/user/user-00.png";

import { InfiniteLoader } from "react-virtualized";

const fetchBlogs = async ({ pageParam = 1 }) => {
    const url = route("users.blogs.index", { page: pageParam });
    const response = await ApiService.get(url);
    return response;
};

export default function Blogs() {
    const { props: sharedProps } = usePage();
    const { buckets, twitterUsername, appUrl, tenantSlug } = sharedProps;

    const canCreateBlog = sharedProps.auth.user?.permissions?.some(
        (perm) => perm.permission_name === "create_blogs",
    );

    const profileImagesBucket = buckets["profile_images"].name;
    const profileImagesRegion = buckets["profile_images"].region;
    const profileImageBaseUrl = `https://${profileImagesBucket}.s3.${profileImagesRegion}.amazonaws.com/${tenantSlug}/`;

    const [postCarouselModal, setPostCarouselModal] = useState(false);
    const [postCarouselModalImages, setPostCarouselModalImages] = useState([]);

    const [sharePostModal, setSharePostModal] = useState(false);
    const [sharePostModalLink, setSharePostModalLink] = useState("");
    const [sharePostMessage, setSharePostMessage] = useState("");
    const [copiedToClipboard, setCopiedToClipdBoard] = useState(false);

    const [editPostModalOpen, setEditPostModal] = useState(false);
    const [postBeingEdited, setPostBeingEdited] = useState(null);

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
        useInfiniteQuery({
            queryKey: ["blogs"],
            queryFn: fetchBlogs,
            getNextPageParam: (lastPage) =>
                lastPage.current_page < lastPage.last_page
                    ? lastPage.current_page + 1
                    : undefined,
            select: (data) => {
                // Patch only the very first post in the entire list, but also force a new object reference for virtualization
                if (data.pages.length > 0 && data.pages[0].data.length > 0) {
                    const patchedFirstPost = {
                        ...data.pages[0].data[0],
                        likes_count: 0,
                        liked_by_user: false,
                    };
                    const patchedFirstPage = {
                        ...data.pages[0],
                        data: [
                            patchedFirstPost,
                            ...data.pages[0].data.slice(1),
                        ],
                    };
                    // Also force new array/object references for all pages to trigger re-render
                    return {
                        ...data,
                        pages: [
                            patchedFirstPage,
                            ...data.pages.slice(1).map((page) => ({
                                ...page,
                                data: [...page.data],
                            })),
                        ],
                    };
                }
                return data;
            },
        });

    const allBlogs = data?.pages.flatMap((page) => page.data) || [];

    const firstBlog = allBlogs && allBlogs.length > 0 ? allBlogs[0] : null;
    const itemCount = hasNextPage ? allBlogs.length + 1 : allBlogs.length;

    const isItemLoaded = (index) => index < allBlogs.length;
    const loadMoreItems = isFetchingNextPage ? () => {} : fetchNextPage;

    return (
        <AuthenticatedLayout scrollable={false}>
            <CarouselModal
                images={postCarouselModalImages}
                modalOpen={postCarouselModal}
                setPostCarouselModal={setPostCarouselModal}
            />
            <SharePostModal
                isOpen={sharePostModal}
                onClose={() => setSharePostModal(false)}
                sharePostModalLink={sharePostModalLink}
                sharePostMessage={sharePostMessage}
                copiedToClipboard={copiedToClipboard}
                setCopiedToClipboard={setCopiedToClipdBoard}
            />
            <EditPostModal
                isOpen={editPostModalOpen}
                onClose={() => setEditPostModal(false)}
                post={postBeingEdited} // Pass the post data to the modal
            />
            <Head title="Blogs" />
            <div className="h-screen overflow-hidden">
                <div className="flex flex-col xl:flex-row gap-6 max-w-[1104px] mx-auto w-full h-full">
                    <div className="w-full max-w-[720px] flex-shrink mx-auto xl:mx-0  pb-5 px-4 xl:px-0">
                        <div
                            className="mt-6"
                            style={{ height: "calc(88vh)", overflowY: "auto" }}
                        >
                            {isLoading ? (
                                <>
                                    <PostSkelotonCard />
                                    <PostSkelotonCard />
                                    <PostSkelotonCard />
                                </>
                            ) : (
                                <InfiniteLoader
                                    isRowLoaded={({ index }) =>
                                        isItemLoaded(index)
                                    }
                                    loadMoreRows={loadMoreItems}
                                    rowCount={itemCount}
                                >
                                    {({ onRowsRendered, registerChild }) => (
                                        <AutoSizer>
                                            {({ height, width }) => {
                                                const rowHeight = 400; // Approx. BlogCard height

                                                const handleScroll = (e) => {
                                                    const scrollTop =
                                                        e.target.scrollTop;
                                                    const clientHeight =
                                                        e.target.clientHeight;

                                                    const startIndex =
                                                        Math.floor(
                                                            scrollTop /
                                                                rowHeight,
                                                        ) * 2;
                                                    const stopIndex = Math.min(
                                                        itemCount - 1,
                                                        Math.ceil(
                                                            (scrollTop +
                                                                clientHeight) /
                                                                rowHeight,
                                                        ) * 2,
                                                    );

                                                    onRowsRendered({
                                                        startIndex,
                                                        stopIndex,
                                                    });
                                                };

                                                return (
                                                    <div
                                                        ref={registerChild}
                                                        style={{
                                                            height,
                                                            width,
                                                            overflow: "auto",
                                                        }}
                                                        onScroll={handleScroll}
                                                    >
                                                        {/* Sticky or scrolling "Latest Blog" header */}
                                                        <div className="mb-6 relative z-10">
                                                            {/* Overlapping Metadata Content */}
                                                            <div className=" mx-auto p-6 bg-white shadow-md rounded-lg relative z-20 dark:bg-boxdark">
                                                                {firstBlog ? (
                                                                    <>
                                                                        <Link
                                                                            href={route(
                                                                                "users.blogs.show",
                                                                                {
                                                                                    blog_url_name:
                                                                                        firstBlog.url_name,
                                                                                },
                                                                            )}
                                                                        >
                                                                            <BlogPreviewCarousel
                                                                                images={
                                                                                    firstBlog.imageUrls
                                                                                }
                                                                            />
                                                                        </Link>
                                                                        {/* Title */}
                                                                        <h1 className="text-2xl md:text-3xl font-bold text-center text-primary pt-5">
                                                                            <Link
                                                                                href={route(
                                                                                    "users.blogs.show",
                                                                                    {
                                                                                        blog_url_name:
                                                                                            firstBlog.url_name,
                                                                                    },
                                                                                )}
                                                                            >
                                                                                {firstBlog
                                                                                    .title
                                                                                    .length >
                                                                                85
                                                                                    ? `${firstBlog.title.slice(0, 85)}...`
                                                                                    : firstBlog.title}
                                                                            </Link>
                                                                        </h1>
                                                                        {/* Author Info */}
                                                                        <div className="flex items-center justify-center mt-4 space-x-2">
                                                                            <Link
                                                                                style={{
                                                                                    marginLeft:
                                                                                        "4px",
                                                                                }}
                                                                                href={route(
                                                                                    "user.users.show",
                                                                                    {
                                                                                        username:
                                                                                            firstBlog
                                                                                                .user
                                                                                                .userName,
                                                                                    },
                                                                                )}
                                                                            >
                                                                                <img
                                                                                    src={
                                                                                        firstBlog
                                                                                            .user
                                                                                            .avatarImageFileName
                                                                                            ? profileImageBaseUrl +
                                                                                              firstBlog
                                                                                                  .user
                                                                                                  .avatarImageFileName
                                                                                            : UserOne
                                                                                    }
                                                                                    alt="Author"
                                                                                    className="w-8 h-8 rounded-full"
                                                                                />
                                                                            </Link>
                                                                            {(firstBlog
                                                                                ?.user
                                                                                ?.firstName ||
                                                                                firstBlog
                                                                                    .user
                                                                                    ?.lastName) && (
                                                                                <span className="text-sm dark:text-bodydark-light">
                                                                                    <b>
                                                                                        Written
                                                                                        by
                                                                                    </b>
                                                                                </span>
                                                                            )}
                                                                            <Link
                                                                                style={{
                                                                                    marginLeft:
                                                                                        "4px",
                                                                                }}
                                                                                href={route(
                                                                                    "user.users.show",
                                                                                    {
                                                                                        username:
                                                                                            firstBlog
                                                                                                .user
                                                                                                .userName,
                                                                                    },
                                                                                )}
                                                                            >
                                                                                <span className="text-sm font-medium text-primary">
                                                                                    {(firstBlog
                                                                                        ?.user
                                                                                        ?.firstName ||
                                                                                        "") +
                                                                                        " " +
                                                                                        (firstBlog
                                                                                            ?.user
                                                                                            ?.lastName ||
                                                                                            "")}
                                                                                </span>
                                                                            </Link>
                                                                            <span
                                                                                style={{
                                                                                    marginLeft:
                                                                                        "4px",
                                                                                }}
                                                                                className=" text-sm text-body dark:text-bodydark-light"
                                                                            >
                                                                                {firstBlog.created_at &&
                                                                                    new Date(
                                                                                        firstBlog.created_at,
                                                                                    ).toLocaleDateString()}
                                                                            </span>
                                                                        </div>
                                                                        {/* Social Buttons */}
                                                                        <div className="flex justify-center mt-6 gap-4">
                                                                            <a
                                                                                href={`https://www.facebook.com/sharer/sharer.php?u=${appUrl}/blogs/${firstBlog.url_name}`}
                                                                            >
                                                                                <button className="text-base  bg-[#3b5998] hover:bg-[#334d84] text-white font-semibold py-2 px-4 rounded">
                                                                                    Facebook
                                                                                </button>
                                                                            </a>
                                                                            <a
                                                                                href={`https://twitter.com/intent/tweet?via=${twitterUsername}&text=${firstBlog.title}`}
                                                                            >
                                                                                <button className="bg-[#1da1f2] hover:bg-[#0d8de5] text-white font-semibold py-2 px-4 rounded">
                                                                                    Twitter
                                                                                </button>
                                                                            </a>
                                                                            <a
                                                                                href={`https://www.linkedin.com/shareArticle?&url=${appUrl}/blogs/${firstBlog.url_name}`}
                                                                            >
                                                                                <button className="bg-[#0077b5] hover:bg-[#005f8f] text-white font-semibold py-2 px-4 rounded">
                                                                                    LinkedIn
                                                                                </button>
                                                                            </a>
                                                                        </div>
                                                                        <hr className="mt-5 mx-auto border-t border-gray-400 opacity-30" />
                                                                        {/* Preview Text */}
                                                                        <p className="mt-4 text-center text-sm text-body dark:text-bodydark-light leading-relaxed">
                                                                            {firstBlog
                                                                                .preview_text
                                                                                .length >
                                                                            200
                                                                                ? `${firstBlog.preview_text.slice(0, 200)}...`
                                                                                : firstBlog.preview_text}
                                                                        </p>
                                                                        {/* Action Button */}
                                                                        <Link
                                                                            href={route(
                                                                                "users.blogs.show",
                                                                                {
                                                                                    blog_url_name:
                                                                                        firstBlog.url_name,
                                                                                },
                                                                            )}
                                                                        >
                                                                            <div className="flex justify-center gap-4 mt-8">
                                                                                <button className=" text-sm bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded">
                                                                                    READ
                                                                                    MORE
                                                                                </button>
                                                                            </div>
                                                                        </Link>
                                                                    </>
                                                                ) : (
                                                                    <div className="text-center text-gray-500 py-12">
                                                                        No blogs
                                                                        found.
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div
                                                            className="grid grid-cols-1 md:grid-cols-2 gap-6"
                                                            style={{
                                                                minHeight:
                                                                    height,
                                                            }}
                                                        >
                                                            {canCreateBlog && (
                                                                <div className="rounded-sm border border-dashed border-primary bg-white shadow-default dark:border-primary dark:bg-boxdark flex flex-col items-center justify-center min-h-[320px]">
                                                                    <Link
                                                                        href={route(
                                                                            "blogs.create",
                                                                        )}
                                                                        className="flex flex-col items-center justify-center w-full h-full p-8 hover:bg-primary/5 transition"
                                                                    >
                                                                        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                                                                            <svg
                                                                                width="36"
                                                                                height="36"
                                                                                fill="none"
                                                                                viewBox="0 0 24 24"
                                                                                stroke="currentColor"
                                                                                className="text-primary"
                                                                            >
                                                                                <path
                                                                                    strokeLinecap="round"
                                                                                    strokeLinejoin="round"
                                                                                    strokeWidth={
                                                                                        2.5
                                                                                    }
                                                                                    d="M12 4v16m8-8H4"
                                                                                />
                                                                            </svg>
                                                                        </div>
                                                                        <span className="text-lg font-semibold text-primary mb-1">
                                                                            Create
                                                                            New
                                                                            Blog
                                                                        </span>
                                                                        <span className="text-gray-500 text-sm text-center">
                                                                            Share
                                                                            your
                                                                            thoughts,
                                                                            stories,
                                                                            or
                                                                            updates
                                                                            with
                                                                            a
                                                                            new
                                                                            blog
                                                                            post.
                                                                        </span>
                                                                    </Link>
                                                                </div>
                                                            )}

                                                            {Array.from({
                                                                length: itemCount,
                                                            }).map((_, idx) => {
                                                                const blog =
                                                                    allBlogs[
                                                                        idx
                                                                    ];
                                                                const key =
                                                                    blog?.id ??
                                                                    `skeleton-${idx}`;
                                                                return (
                                                                    <div
                                                                        key={
                                                                            key
                                                                        }
                                                                    >
                                                                        {isItemLoaded(
                                                                            idx,
                                                                        ) ? (
                                                                            <BlogCard
                                                                                blog={
                                                                                    blog
                                                                                }
                                                                            />
                                                                        ) : (
                                                                            <PostSkelotonCard />
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            }}
                                        </AutoSizer>
                                    )}
                                </InfiniteLoader>
                            )}
                            {isFetchingNextPage && <p>Loading more blogs...</p>}
                        </div>
                    </div>
                    {/* Second Column */}
                    <div className="hidden xl:flex flex-col w-full max-w-[350px] rounded h-full min-h-0 overflow-y-auto">
                        <div className="pt-5 space-y-4 pb-25">
                            <NewOrganizations />
                            <NewBlogs />
                        </div>
                    </div>
                </div>
            </div>
        </AuthenticatedLayout>
    );
}
